/**
 * VisibilityFilters — global toggles for showing BETA and PLANNED nav items.
 * BETA defaults ON, PLANNED defaults OFF. Persisted in localStorage.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'ff:nav:visibility:v1';

const DEFAULTS = { showBeta: true, showPlanned: false };

const Ctx = createContext({
  ...DEFAULTS,
  setShowBeta: () => {},
  setShowPlanned: () => {},
});

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      showBeta: typeof parsed.showBeta === 'boolean' ? parsed.showBeta : DEFAULTS.showBeta,
      showPlanned: typeof parsed.showPlanned === 'boolean' ? parsed.showPlanned : DEFAULTS.showPlanned,
    };
  } catch {
    return DEFAULTS;
  }
}

export function VisibilityFiltersProvider({ children }) {
  const [showBeta, setShowBeta] = useState(() => load().showBeta);
  const [showPlanned, setShowPlanned] = useState(() => load().showPlanned);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ showBeta, showPlanned })); } catch {}
  }, [showBeta, showPlanned]);

  const toggleBeta = useCallback(() => setShowBeta(v => !v), []);
  const togglePlanned = useCallback(() => setShowPlanned(v => !v), []);

  const value = useMemo(() => ({
    showBeta, showPlanned,
    setShowBeta, setShowPlanned,
    toggleBeta, togglePlanned,
  }), [showBeta, showPlanned, toggleBeta, togglePlanned]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVisibilityFilters() {
  return useContext(Ctx);
}

/** Convenience predicate: should this nav item be shown given current toggles? */
export function isItemVisible(item, { showBeta, showPlanned }) {
  if (item?.beta && !showBeta) return false;
  if (item?.planned && !showPlanned) return false;
  return true;
}
